<template>
    <b-container fluid class="h-100 p-0 bg-gray d-flex flex-column">
        <b-row v-if="!displayForm" class="m-0 p-0">
            <b-col xs="12" sm="12" md="12" lg="12" class="d-flex justify-content-between align-items-start">
                <h2 class="mx-2 my-4">{{ $t('DistributorCertificate') }}</h2>
                <b-button-toolbar class="d-flex justify-content-end align-items-center p-2">
                    <!-- FILTRE DE LA LISTE DES MANDATS POUR AFFICHER LES ARCHIVES -->
                    <b-form-checkbox
                        @change="showArchived"
                        switch
                        :variant="Variant"
                        :class="'pr-2 mr-2 border-right d-flex align-items-center border-' + Variant">
                        {{ $t('ShowObsoleteCertificates') }}
                    </b-form-checkbox>
                    <!-- BOUTON CREATION CONTRAT CLIENT -->
                    <b-button 
                        @click="showClientContract = true"
                        variant="outline-black" 
                        pill 
                        v-b-tooltip.html
                        :title="$t('CreateClientContract')"
                        class="mx-1">
                        <font-awesome-icon icon="fa-solid fa-file-circle-plus" />
                    </b-button>
                    <!-- BOUTON IMPORT MANDATS FORMAT EXCEL -->
                    <b-button 
                        @click="ShowImportCertificate"
                        variant="outline-black" 
                        pill 
                        v-b-tooltip.html
                        :title="$t('ImportCertificates')"
                        class="mx-1">
                        <font-awesome-icon icon="fa-solid fa-file-import" />
                    </b-button>
                    <!-- BOUTON EXPORT MANDATS FORMAT EXCEL -->
                    <b-button 
                        @click="ShowDownloadReport"
                        variant="outline-black" 
                        pill 
                        v-b-tooltip.html
                        :title="$t('ExportCertificates')"
                        class="mx-1">
                        <font-awesome-icon icon="fa-file-excel" />
                    </b-button>
                    <!-- BOUTON CREATION NOUVEAU MANDAT -->
                    <b-button 
                        @click="DisplayForm(null)"
                        :variant="Variant" 
                        pill 
                        v-b-tooltip.html
                        :title="$t('CreateCertificate')"
                        class="mx-1">
                        <font-awesome-icon icon="fa-solid fa-plus" />
                    </b-button>
                </b-button-toolbar>
            </b-col>
        </b-row>
        <b-row v-if="!displayForm" class="m-0 pb-3 flex-grow-1 overflow-auto">
            <b-col cols="12" md="8" lg="8">
                <!-- COMPOSANT LISTANT LES MANDATS -->
                <CertificateList 
                    @OnDownLoad="OnDownLoadCertificate"
                    @OnArchive="ArchiveCertificate"
                    @OnRollBack="RollBackCertificate"
                    @OnDelete="DeleteCertificate"
                    @OnSelect="SetCertificate" 
                    @OnEdit="DisplayForm"
                    @OnClientValidation="OpenClientValidation"
                    @OnSupervisorValidation="OpenSupervisorValidation"
                    @OnShowFaq="showFaq = !showFaq",
                    @OnTechnicalData="OpenTechnicalData"
                    :Items.sync="items" />
            </b-col>
            <b-col cols="12" md="4" lg="4">
                <!-- COMPOSANT AFFICHANT MANDAT SELECTIONNE DANS LA LISTE -->
                <CertificateDisplay 
                    @RenewSubscribtionEvent="RenewSubscribtionEvent"
                    @RenewCertificateEvent="RenewCertificateEvent"
                    @DownloadResource="OnDownLoadResource"
                    @DeleteResource="OnDeleteResource"
                    @OnRefreshMeter="OnRefreshMeter"
                    :Certificate.sync="selectedCertificate" 
                    :Forms="forms"/>
            </b-col>
        </b-row>
        <b-row v-else class="m-0 p-0 h-100">
            <b-col xs="12" sm="12" md="12" lg="12" class="p-0">
                <CertificateForm 
                    @Cancel="OnCancel"
                    @Validation="displayForm = false"
                    @OnRefreshMeter="OnRefreshMeter"
                    :Forms="forms" 
                    :Mode="mode" 
                    :Show.sync="displayForm" 
                    :Certificate.sync="selectedCertificate" />
            </b-col>
        </b-row>
        <!-- COMPOSANT DE SIGNATURE MANDAT -->
        <SignupCertificateOptions 
            :Certificate="selectedCertificate"
            :Visible.sync="showSignup"
            :Who="whoIsSigning"/>
        <!-- MODAL DE VALIDATION SUPERVISEUR PAR MOT DE PASSE (MANDAT SANS CHAINE DE SIGNATURE, EX : SOLAREDGE) -->
        <b-modal
            :visible="showSupervisorPasswordValidation"
            @hide="showSupervisorPasswordValidation = false"
            size="md"
            centered
            hide-footer
            no-close-on-backdrop
            no-close-on-esc
            :title="$t('SupervisorValidation')">
            <PasswordValidation
                :isSending="supervisorValidationSending"
                @Submit="OnSupervisorPasswordValidation"
                @Back="showSupervisorPasswordValidation = false"/>
        </b-modal>
        <!-- COMPOSANT DE FAQ -->
        <FAQComponent 
            :Show.sync="showFaq" 
            :Title="'FAQ - Mandat'" 
            :Faqs="faqs" 
            :Variant="Variant"/>
        <!-- COMPOSANT SELECTEUR MANDATS -->
        <CertificateSelector 
            @Submit="OnDownloadReport" 
            :Show.sync="showCertificateSelector" 
            :Variant="Variant"
            :Certificates="items" />
        <!-- COMPOSANT POUR L'IMPORT EN MASSE -->
         <CertificatesImport
            :Show.sync="showImportCertificate"
            :Variant="Variant"/>
        <!-- COMPOSANT DE SAISIE DES DONNEES TECHNIQUES DES COMPTEURS -->
        <TechnicalDataForm
            :Show.sync="showTechnicalData"
            :Certificate="selectedCertificate"/>
        <!-- COMPOSANT DE GESTION DES CONTRATS CLIENT -->
        <ClientContractForm
            :Show.sync="showClientContract"/>
    </b-container>
</template>
<script lang="ts">

    /**
     * COMPOSANTS VUE JS
     */
    import { Component, InjectReactive, Vue } from 'vue-property-decorator';
	import { Getter} from 'vuex-class';
    import { mixins } from 'vue-class-component';
    
    /**
     * Classes utilitaires
     */
    import Dictionary from '@/types/Dictionary';

    /**
     * Composants mandats
     */
    import CertificateList from './SMAForm/CertificateList.vue';
    import CertificateForm from './SMAForm/CertificateForm.vue';
    import CertificateDisplay from './SMAForm/CertificateDisplay.vue';
    import SignupCertificateOptions from './SMAForm/SignupCertificateOptions.vue';
import SMALifecycleStepEnum from '@/enums/Administration/SMA/SMALifecycleStepEnum';
    import PasswordValidation from './SMAForm/SignupOptions/PasswordValidation.vue';
    import { SMAUtils } from './SMAForm/SMAUtils';
    import FAQComponent from '@/components/commons/FAQComponent.vue';

    /**
     * Classes et Enums
     */
    import { Resource, SMACertificate } from '@/types/Administration/SMA/SMACertificate';
    import { SMADistributorForm } from '@/types/Administration/SMA/SMADistributorForm';

    /**
     * Traduction
     */
    import locale from './locales/SMA.json';

    /**
     * Classe et enumération Téléchargement
     */
    import DownloadStateEnum from '@/enums/DownloadStateEnum';
    import Download from '@/types/Download';
    import { FAQ } from '@/types/Commons/FAQ';
    import CertificateSelector from './SMAForm/CertificateSelector.vue';
    import CertificatesImport from './SMAForm/CertificatesImport.vue';
    import TechnicalDataForm from './SMAForm/TechnicalDataForm.vue';
    import ClientContractForm from './SMAForm/ClientContractForm.vue';

	@Component({
		components: {
            CertificateList,
            CertificateDisplay,
            CertificateForm,
            SignupCertificateOptions,
            PasswordValidation,
            FAQComponent,
            CertificateSelector,
            CertificatesImport,
            TechnicalDataForm,
            ClientContractForm
        },
        i18n : { messages: locale }
	})
    /**
     * Composant racine pour la gestion des mandats distributeurs
     */
	export default class CertificateAdministration extends mixins (Vue, SMAUtils) {

        //#region DONNEES

        /**
         * Liste des formulaires
         * Determine l'architecture des formulaires et besoins pour chaques distributeur
         * Est créer à partir des Metadonnées de classe Manager dans chaque projet distributeur (DCx.ENEDIS, DCx.GRDF, DCx.SRD, Etc...)
         * Elements récupérer dans le EndPoint "GetSMAForms" du controller "SMAController"
         */
        @Getter('CertificateStore/GetForms')
        public forms!: Dictionary<SMADistributorForm>;

        /**
         * Liste des mandats récupéré dans le store 'CertificateStore'
         */
		@Getter('CertificateStore/GetCertificates')
        public items!: Array<SMACertificate>;

        /**
         * Mandat sélectionné pour modification dans le composant "CertificateForm"
         */
		@Getter('CertificateStore/GetCurrentCertificate')
        private storedSelectedCertificate!: SMACertificate;
        
        // Getter
        get selectedCertificate(): SMACertificate {
            return this.storedSelectedCertificate;
        }

        // Setter
        set selectedCertificate(value: SMACertificate) {
            this.$store.dispatch("CertificateStore/SetCurrentCertificate", value);
        }

        /** 
         * Variant de couleur à appliquer sur le composant 
         */
        @InjectReactive()
        public Variant!: string;

        /**
         * FAQ selectionné
         */
        private faqs: Array<FAQ> = [];

        /**
         * Affichage de la fenêtre FAQ
         */
        private showFaq: boolean = false;

        /**
         * Affichage du formulaire de mandat dans le composant "CertificateDisplay"
         */
        private displayForm: boolean = false

        /**
         * Mode du formulaire ("New" pour nouveau mandat, "Edit" pour modification, "Renew" pour renouvellement)
         */
        private mode: "New" | "Edit" | "Renew" = "New";

        /**
         * Affichage de la fenêtre de signature
         */
        private showSignup: boolean = false;
        private showSupervisorPasswordValidation: boolean = false;
        private supervisorValidationSending: boolean = false;
        private showTechnicalData: boolean = false;
        private showClientContract: boolean = false;
        
        /**
         * Personne qui réalise la signature
         * Client ou superviseur
         */
        private whoIsSigning: "Customer" | "Supervisor" = "Customer"; 

        /**
         * Affichage du composant pour la sélection de mandats
         */
        private showCertificateSelector: boolean = false;

        /**
         * Affichage du composant pour l'import de mandat en masse
         */
        private showImportCertificate: boolean = false;
        
        //#endregion
        //#region ACTIONS TABLEAU MANDATS
        
        /**
         * Archivage d'un mandat si validé par le client
         * Toaster du message de retour serveur
         * @param item Mandat à archiver
         */
        private async ArchiveCertificate(item: SMACertificate): Promise<void>
        {
            await this.$store.dispatch("CertificateStore/Save", { item, action: "Archive" }).then((data) => {
                this.showToast(data.message, data.success ? "success" : "danger", "SuccessArchive");
                this.selectedCertificate = null;
            });
        }

        /**
         * Retour en arrière sur l'avancement du mandat
         * Si le mandat est au status Validation client ou Validation superviseur, le renvoi au status de création
         * @param item Mandat a Rollback
         */
        private async RollBackCertificate(item: SMACertificate): Promise<void>
        {
            await this.$store.dispatch("CertificateStore/Save", { item, action: "RollBack" }).then((data) => {
                this.showToast(data.message, data.success ? "success" : "danger", "RollBack");
                this.selectedCertificate = item;
            });
        }

        /**
         * Suppression du mandat si pas validé par le client
         * Toaster du message de retour serveur
         * @param item Mandat à supprimer
         */
        private async DeleteCertificate(item: SMACertificate): Promise<void>
        {
            await this.$store.dispatch("CertificateStore/Delete", { item }).then((data) => {
                this.showToast(data.message, data.success ? "success" : "danger", "DeleteSuccess");
                this.selectedCertificate = null;
            });
        }

        /**
         * Gestion de l'événement de renouvellement d'abonnement
         * @param item Mandat
         * @param id Identifiant compteur
         */
        private async RenewSubscribtionEvent(item: SMACertificate, id: string): Promise<void>
        {
            await this.$store.dispatch("CertificateStore/RenewSubscribtion", { item, id }).then((result) => {
                /** Mise à jour de la liste des compteurs avec les nouvelles données*/
                const index = this.selectedCertificate.MeterInformations.Meters.findIndex(x => x.RaepceNumber == result.data.Meters[0].RaepceNumber);
                
                if (index > -1)
                    this.selectedCertificate.MeterInformations.Meters.splice(index, 1, result.data.Meters[0]);

                this.showToast(result.message, result.success ? "success" : "danger", "RefreshComplete");
                this.selectedCertificate = item;
            });
        }

        /**
         * Renouvellement d'un mandat
         * @param item Mandat distributeur
         */
        private async RenewCertificateEvent(item: SMACertificate): Promise<void>
        {
            this.mode = "Renew";
            const newCertificate: SMACertificate = new SMACertificate(item);

            const nbOfYears = this.forms[newCertificate.Distributor].MaxYears ?? 3;

            const today: Date = new Date();
            const inThreeYears: Date = new Date(today);
            inThreeYears.setFullYear(inThreeYears.getFullYear() + nbOfYears);

            Vue.set(newCertificate, "endDate", inThreeYears);
            Vue.set(newCertificate, "resources", []);

            this.$store.dispatch("CertificateStore/SetRenewalCertificate", newCertificate);

            this.displayForm = true;
        }

        /**
         * Affichage du composant pour sélectionner des mandats
         */
        private ShowDownloadReport(): void
        {
            this.showCertificateSelector = true;
        }

        /**
         * Affichage du composant d'import de mandat en masse
         */
        private ShowImportCertificate(): void
        {
            this.showImportCertificate = true;
        }
        /**
         * Téléchargement d'un mandat
         * Toaster du message de retour serveur
         * @param item Mandat à télécharger
         */
        private async OnDownLoadCertificate(item: SMACertificate): Promise<void>
        {
            let index: number = this.$store.getters['DownloadingStore/GetLastIndex'];
            let download: Download = {
                Index: ++index, 
                Name: item.Name + ".pdf", 
                Type: ".pdf", 
                State: DownloadStateEnum.InProgress, 
                Data: "", 
                Direct: true
            }

            this.$store.dispatch('DownloadingStore/AddDownloading', download);
            
            await this.$store.dispatch("CertificateStore/Generate", { item, resources: [item.Resources.find(p => p.Type == '6') ?? item.Resources[0]] }).then((response) => {
                this.$store.dispatch('DownloadingStore/AddData', {index, data: response.data});
                this.$store.dispatch('DownloadingStore/UpdateState', {index, state: DownloadStateEnum.Finished});
            });
        }

        /**
         * Téléchargement d'une ressource d'un mandat
         * Toaster du message de retour serveur
         * @param item Mandat à télécharger
         * @param resource Ressource à télécharger
         */
        private async OnDownLoadResource(item: SMACertificate, resource: Resource): Promise<void>
        {
            let index: number = this.$store.getters['DownloadingStore/GetLastIndex'];
            let download: Download = {
                Index: ++index, 
                Name: resource.Name + ".pdf", 
                Type: resource.Extension, 
                State: DownloadStateEnum.InProgress, 
                Data: resource.Data, 
                Direct: true
            }

            this.$store.dispatch('DownloadingStore/AddDownloading', download);
            
            await this.$store.dispatch("CertificateStore/Download", { item, resources: [resource] }).then((response) => {
                this.$store.dispatch('DownloadingStore/AddData', {index, data: response.data});
                this.$store.dispatch('DownloadingStore/UpdateState', {index, state: DownloadStateEnum.Finished});
            });
        }

        /**
         * Pour le téléchargement du compte rendu des récupérations de données de mandat
         * @param items Liste des mandats
         */
        private async OnDownloadReport(items: SMACertificate[]): Promise<void>{
            if(items.length == 0){
                this.showToast("PleaseSelectCertificate", "warning", "Export");
                return;
            }

            let index: number = this.$store.getters['DownloadingStore/GetLastIndex'];
            let download: Download = {
                Index: ++index, 
                Name: "rapport_mandat.xlsx", 
                Type: ".xslx", 
                State: DownloadStateEnum.InProgress, 
                Data: null, 
                Direct: false
            }

            this.$store.dispatch('DownloadingStore/AddDownloading', download);
            
            await this.$store.dispatch("CertificateStore/DownloadReport", { items }).then((response) => {
                this.$store.dispatch('DownloadingStore/AddData', {index: download.Index, data: response.data});
                this.$store.dispatch('DownloadingStore/UpdateState', {index: download.Index, state: DownloadStateEnum.Finished});
            });
        }

        /**
         * Gestion de l'événement de suppression d'une annexe d'un mandat
         * @param item Mandat
         * @param resource Ressource à supprimer
         */
        private async OnDeleteResource(item: SMACertificate, resource: Resource) : Promise<void>
        {
            const data = await this.$store.dispatch("CertificateStore/DeleteResource", { item, resource });
            this.showToast(data.message, data.success ? "success" : "danger", "DeleteResource");
            this.selectedCertificate = item;
        }

        /**
         * Rafraichissement des données d'un compteur
         * @param Id Numéro d'identifiant du compteur à rafraichir
         */
        private async OnRefreshMeter(Id: string) : Promise<void>
        {
            this.$store.dispatch("CertificateStore/RefreshMeter", { Certificate: this.selectedCertificate, Id: Id }).then((result) => {
                this.showToast(result.message, result.success ? "success" : "danger", "RefreshComplete");
            });
        }

        /**
         * Lors de la validation client on ouvre la fenêtre de signature avec le pdf
         * @param item Mandat à signer
         */
        private async OpenClientValidation(item: SMACertificate): Promise<void>
        {
            this.whoIsSigning = "Customer";
            this.selectedCertificate = item;
            this.showSignup = true;
        }
        
        /**
         * Lors de la validation superviseur on ouvre la fenêtre de signature avec le pdf
         * @param item Mandat à signer
         */
        private async OpenSupervisorValidation(item: SMACertificate): Promise<void>
        {
            // Flux sans chaîne de signature (ex : SolarEdge) :
            // validation finale du superviseur par mot de passe uniquement, sans PDF
            if (!this.hasClientSignature(item.Distributor))
            {
                this.selectedCertificate = item;
                this.showSupervisorPasswordValidation = true;
                return;
            }

            this.whoIsSigning = "Supervisor";
            this.selectedCertificate = item;
            this.showSignup = true;
        }

        /**
         * Validation finale par mot de passe d'un mandat sans chaîne de signature
         * @param password Mot de passe du superviseur
         */
        private async OnSupervisorPasswordValidation(password: string): Promise<void>
        {
            this.supervisorValidationSending = true;

            const result = await this.$store.dispatch("CertificateStore/SupervisorValidation", {
                Certificate: this.selectedCertificate,
                Password: password
            });

            this.supervisorValidationSending = false;

            this.showToast(result.message, result.success ? "success" : "danger", "Error");

            if (result.success)
                this.showSupervisorPasswordValidation = false;
        }

        /**
         * Indique si le cycle de vie du mandat du distributeur comporte une signature client (mandat PDF)
         * Décrit côté serveur dans SMAAttribute (LifecycleSteps)
         * @param distributor Type distributeur du mandat
         */
        private hasClientSignature(distributor: number) : boolean
        {
            return this.forms[distributor]?.LifecycleSteps?.includes(SMALifecycleStepEnum.ClientValidation) ?? true;
        }

        /**
         * Ouverture de la fenêtre de saisie des données techniques des compteurs
         * Réservée aux superviseurs, mandat signé par le superviseur
         * @param item Mandat selectionné
         */
        private OpenTechnicalData(item: SMACertificate): void
        {
            this.selectedCertificate = item;
            this.showTechnicalData = true;
        }
        
        /**
         * Attribut le mandat selectionné à la variable 'certificate'
         * Pour affichage dans le coposant CertificateDisplayer
         * @param item Certificate selectionné
         */
        private async SetCertificate(item: SMACertificate): Promise<void>
        {
            this.selectedCertificate = item;
        }

        /**
         * Affichage des mandats archivés ou non
         */
        private async showArchived(checked): Promise<void>
        {
            this.$store.dispatch("CertificateStore/ToggleShowArchived", checked);
        }

        /**
         * Affichage de la modal du formulaire des mandats
         * Si mandat null => Mode 'new' pour création
         * Si mandat non null => Mode 'edit' pour édition
         * @param item Certificat
         */
        private DisplayForm(item: SMACertificate) : void {
            this.mode = item ? "Edit" : "New";
            this.selectedCertificate = new SMACertificate(item ?? {});
            this.displayForm = true;
        }

        /**
         * Retourne la liste des distributeurs avec une FAQ
         */
        private getFAQs() : Array<FAQ> {
            return Object.keys(this.forms).filter(key => this.forms[key].FaqResourcePath).map(key => ({
                id: this.forms[key].Type,
                title: this.forms[key].Name,
                path: this.forms[key].FaqResourcePath,
                themeFaq: [],
            }));
        }

        /**
         * Gestion de l'événement d'annulation dans le formulaire de mandat
         */
        private OnCancel(): void
        {
            this.displayForm = false;
            this.selectedCertificate = null;
            this.showArchived(false);
        }

        //#endregion

        //#region METHODES COMPOSANT

        /**
         * Création du composant
         * Chargement des données lié aux formulaires distributeurs
         * Chargement de la liste des mandats
         */
        private async mounted(){
            await this.$store.dispatch("CertificateStore/LoadSMAForms").then((data) => {
                if(!data.success)
                    this.showToast(data.message, "danger", "Error");
                this.faqs = this.getFAQs(); // => Sélection du premier distributeur avec FAQ pour affichage à l'ouverture de la modal FAQ
            }); // => Chargement des formulaires Distributeurs
            await this.$store.dispatch("CertificateStore/Load").then((data) => {
                if(!data.success)
                    this.showToast(data.message, "danger", "Error");
            }); // => Chargement de la liste des mandats Distributeur
        }

        /**
         * Destruction du composant
         * Suppression des données du store Mandat
         */
        private destroy(): void {
            this.$store.dispatch("CertificateStore/Clear");
        }
        //#endregion
	}
</script>
<style scoped>


/* Met à jour la couleur de fond du switch du sélecteur de work lorsqu'il est coché */
:deep(.custom-control-input:checked ~ .custom-control-label::before) {
    border-color: var(--Variant) !important;
    background-color: var(--Variant) !important;
}

/* Met à jour la couleur de fond du switch du sélecteur de work lorsqu'il est actif */
:deep(.custom-control-input:active ~ .custom-control-label::before) {
    background-color: var(--Variant) !important;
    border-color: var(--Variant) !important;
}

</style>